import { ChatModel } from '@/api/models';
import { chatModels } from '@/constants/models';

const MODEL_EFFORT_STORAGE_KEY = 'sms-gpt_model_effort';

const EFFORT_LABELS: Record<string, string> = {
  low: 'Instant',
  medium: 'Standard',
  high: 'Complex'
};

// A model object can travel through state (or come back from the API) without its
// configuration, so the levels are always resolved against the model catalogue.
export const getEffortLevels = (model: ChatModel | null): string[] => {
  if (!model) return [];
  if (model.reasoning_levels?.length) return model.reasoning_levels;
  return chatModels.find((item) => item.id === model.id)?.reasoning_levels || [];
};

export const supportsReasoningEffort = (model: ChatModel | null): boolean => getEffortLevels(model).length > 0;

export const getEffortLabel = (effort: string): string => EFFORT_LABELS[effort] || '';

// The catalogue value is the model's own default; the middle level is the fallback when a
// model lists levels without naming one of them as its default.
const getDefaultEffort = (model: ChatModel): string => {
  const effortLevels = getEffortLevels(model);
  if (!effortLevels.length) return '';
  const configuredEffort = chatModels.find((item) => item.id === model.id)?.reasoning || '';
  return effortLevels.includes(configuredEffort) ? configuredEffort : effortLevels[Math.floor(effortLevels.length / 2)];
};

export const getSelectedEffort = (model: ChatModel | null): string => {
  if (!model) return '';
  const effortLevels = getEffortLevels(model);
  if (!effortLevels.length) return '';
  return effortLevels.includes(model.reasoning) ? model.reasoning : getDefaultEffort(model);
};

export const withReasoningEffort = (model: ChatModel, effort: string): ChatModel => {
  const effortLevels = getEffortLevels(model);
  if (!effortLevels.length) return model;
  return {
    ...model,
    reasoning_levels: effortLevels,
    reasoning: effortLevels.includes(effort) ? effort : getDefaultEffort(model)
  };
};

const getStoredEffortMap = (): Record<string, string> => {
  if (typeof window === 'undefined') return {};

  try {
    const raw = localStorage.getItem(MODEL_EFFORT_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const setStoredEffortMap = (effortMap: Record<string, string>) => {
  if (typeof window === 'undefined') return;
  localStorage.setItem(MODEL_EFFORT_STORAGE_KEY, JSON.stringify(effortMap));
};

export const applyPersistedModelSelection = (model: ChatModel | null): ChatModel | null => {
  if (!model) return model;
  const effortLevels = getEffortLevels(model);
  if (!effortLevels.length) return model;
  const storedEffort = getStoredEffortMap()[model.id];
  return withReasoningEffort(model, effortLevels.includes(storedEffort) ? storedEffort : getSelectedEffort(model));
};

export const persistModelSelection = (model: ChatModel | null) => {
  if (!model) return;
  const selectedEffort = getSelectedEffort(model);
  if (!selectedEffort) return;

  const effortMap = getStoredEffortMap();
  effortMap[model.id] = selectedEffort;
  setStoredEffortMap(effortMap);
};
